import { Slot, Slottable } from '@radix-ui/react-slot'
import { clsx } from 'clsx'
import { forwardRef, useImperativeHandle, useLayoutEffect, useRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { t } from '@/lib/i18n'
import { motionOn } from './motion'
import { ChevronShape } from './shapes'
import { springEasing } from './springs'

export type R5ButtonVariant = 'accent' | 'primary' | 'ghost' | 'quiet' | 'link' | 'danger'
/** One shape, never cut (APP-DESIGN-DIRECTION 3.6): the same element shows idle, working, done and failed. */
export type R5ButtonState = 'idle' | 'loading' | 'success' | 'error'

export interface R5ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * `accent` is yellow with navy text and exists ONCE per screen: the single main action. `primary` is navy, `ghost` is white
   * with a line, `quiet` has no border (rows, toolbars), `link` is text with a chevron that pushes, `danger` is red text on
   * red tint (solid red only inside its own confirm step).
   */
  variant?: R5ButtonVariant
  /** `lg` 52px (student main actions, reward card), `md` 44px (default; 36px in staff density), `sm` 36px (30px staff). */
  size?: 'lg' | 'md' | 'sm'
  /** Render the single child element (for example an `R5Link`) instead of a `<button>`, keeping the button look. */
  asChild?: boolean
  /** The async morph: label to circle and spinner, then a check (or back to the label on error). Buttons only. */
  state?: R5ButtonState
  /** Spoken while `state` is loading or success (and shown instead of the morph under reduced motion). */
  busyLabel?: string
  doneLabel?: string
  /** Full width of its container (phone sheets, the reward card). */
  block?: boolean
  /** A leading icon (an `Icon` or `Glyph`), kept apart from the label so the label can fade on its own. */
  icon?: ReactNode
}

/**
 * The r5 button (APP-DESIGN-DIRECTION 2.6, 3.4). Not `components/ui/button.tsx`, which the exam room uses and which is never
 * edited. A pill with a solid 3px press edge under the face (accent, primary, ghost, danger): pressing collapses the edge
 * and moves the face down 3px in 90 ms, release springs back on `--r5-sp-snappy`. Hover deepens the face colour.
 */
export const R5Button = forwardRef<HTMLButtonElement, R5ButtonProps>(function R5Button(
  { variant = 'primary', size = 'md', asChild = false, state = 'idle', busyLabel, doneLabel, block = false, icon, className, children, type, disabled, ...rest },
  forwarded,
) {
  const Component = asChild ? Slot : 'button'
  const accent = variant === 'accent'
  const ref = useRef<HTMLButtonElement>(null)
  useImperativeHandle(forwarded, () => ref.current as HTMLButtonElement)
  const natural = useRef<number | null>(null)
  const previous = useRef<R5ButtonState>(state)

  // The morph changes the button's width (the one width animation the system allows: the button sits in its own slot).
  useLayoutEffect(() => {
    const element = ref.current
    const before = previous.current
    previous.current = state
    if (!element || asChild || before === state) return
    const round = state === 'loading' || state === 'success'
    const wasRound = before === 'loading' || before === 'success'
    if (round && !wasRound) {
      natural.current = element.offsetWidth
      const height = element.offsetHeight
      if (motionOn() && typeof element.animate === 'function') {
        element.animate([{ width: `${natural.current}px` }, { width: `${height}px` }], { duration: 320, easing: springEasing('snappy'), fill: 'forwards' })
      } else {
        element.style.width = `${height}px`
      }
    } else if (!round && wasRound) {
      const from = element.offsetWidth
      element.getAnimations?.().forEach((animation) => animation.cancel())
      element.style.width = ''
      const to = element.offsetWidth
      if (motionOn() && typeof element.animate === 'function') element.animate([{ width: `${from}px` }, { width: `${to}px` }], { duration: 320, easing: springEasing('snappy') })
    }
  }, [state, asChild])

  const working = state === 'loading' || state === 'success'
  return (
    <Component
      ref={ref}
      className={clsx(
        'r5-btn', `r5-btn--${variant}`, size !== 'md' && `r5-btn--${size}`,
        variant !== 'link' && variant !== 'quiet' && 'r5-press',
        block && 'r5-btn--block',
        state !== 'idle' && `r5-btn--${state}`,
        className,
      )}
      data-r5-accent={accent ? '' : undefined}
      data-state={state === 'idle' ? undefined : state}
      aria-busy={state === 'loading' ? true : undefined}
      disabled={asChild ? disabled : disabled || working || undefined}
      {...(asChild ? {} : { type: type ?? 'button' })}
      {...rest}
    >
      {icon && <span className="r5-btn__icon" aria-hidden="true">{icon}</span>}
      <Slottable>{children}</Slottable>
      {variant === 'link' && (
        <span className="r5-push" aria-hidden="true">
          <ChevronShape />
          <ChevronShape />
        </span>
      )}
      {!asChild && state !== 'idle' && (
        <span className="r5-btn__morph" aria-hidden="true">
          {state === 'loading' && <span className="r5-btn__spinner" />}
          {state === 'success' && <svg className="r5-btn__check" viewBox="0 0 24 24"><path d="M5 12.6l4.4 4.4L19 7.4" pathLength={1} /></svg>}
        </span>
      )}
      {!asChild && working && <span className="r5-sr" role="status">{state === 'loading' ? busyLabel ?? t('Saving') : doneLabel ?? t('Saved')}</span>}
    </Component>
  )
})
